import { Users } from "lucide-react";
import { useForm } from "react-hook-form";
import Button from "../../components/ui/Button";
import Dialog from "../../components/ui/Dialog";
import type { GroupId } from "../../types/demo";

type GroupAssignmentForm = {
  groupIds: GroupId[];
};

interface GroupOption {
  id: GroupId;
  name: string;
  description?: string;
}

interface Props {
  userEmail: string;
  currentGroupIds: GroupId[];
  groups: GroupOption[];
  onSubmit: (values: GroupAssignmentForm) => Promise<void>;
  onClose: () => void;
}

export default ({ userEmail, currentGroupIds, groups, onSubmit, onClose }: Props) => {
  const groupAssignmentForm = useForm<GroupAssignmentForm>({
    defaultValues: { groupIds: currentGroupIds },
  });

  const handleSubmit = groupAssignmentForm.handleSubmit(async values => {
    await onSubmit(values);
  });

  return (
    <Dialog title="Assign to Group" subtitle={userEmail} onClose={onClose} TitleIcon={Users}>
      <form className="space-y-4" onSubmit={event => void handleSubmit(event)}>
        {groups.length > 0 ? (
          <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
            {groups.map(group => (
              <label key={group.id} className="flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 text-sm text-slate-700">
                <div className="min-w-0">
                  <p className="truncate font-medium text-slate-900">{group.name}</p>
                  {group.description && <p className="truncate text-xs text-slate-400">{group.description}</p>}
                </div>
                <input
                  type="checkbox"
                  value={group.id}
                  {...groupAssignmentForm.register("groupIds")}
                  className="h-4 w-4 rounded border-slate-300 text-indigo-600"
                />
              </label>
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
            No groups exist yet. Create a group in <code>Integrations &gt; Groups</code> first.
          </div>
        )}
        <div className="flex justify-end gap-3">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={groupAssignmentForm.formState.isSubmitting || groups.length === 0}>
            {groupAssignmentForm.formState.isSubmitting ? "Saving..." : "Save groups"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};
